import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Control, DatePicker, Input, Clone, Delete, Warning } from 'components';
import {
  TimeWindowFormProps,
  TimeWindowHeaderWrapper,
} from 'components/TimeWindow';
import { CircleButton } from 'components/CircleButton';
import { ReturnComponentSearch } from 'components/Select/ReturnComponentSearch';
import { useFormErrors } from 'hooks';

export const TimeWindowForm: React.FC<TimeWindowFormProps> = ({
  index,
  onClone,
  onRemove,
  refreshDates,
}) => {
  const {
    control,
    register,
    getValues,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();
  const fieldName = `timeWindows[${index}]`;
  const timeWindows = getValues('timeWindows');
  const { getErrorMessage, hasErrors } = useFormErrors({
    errors,
    fieldName,
    index,
  });

  return (
    <div>
      <TimeWindowHeaderWrapper key={fieldName}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignContent: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <Control label='From Date'>
            <Controller
              control={control}
              name={`${fieldName}.fromDate`}
              render={({ field: { onChange, value } }) => {
                return (
                  <DatePicker
                    onChange={onChange}
                    onCalendarClose={() => refreshDates?.()}
                    testid={`${fieldName}.fromDate`}
                    selected={value}
                    errorMessage={getErrorMessage(`${fieldName}.fromDate`)}
                    style={{ width: 130 }}
                  />
                );
              }}
            />
          </Control>
          <Control label='To Date'>
            <Controller
              control={control}
              name={`${fieldName}.toDate`}
              render={({ field: { onChange, value } }) => {
                return (
                  <DatePicker
                    disabled
                    onChange={onChange}
                    selected={value}
                    testid={`${fieldName}.toDate`}
                    errorMessage={getErrorMessage(`${fieldName}.toDate`)}
                    style={{ width: 130 }}
                  />
                );
              }}
            />
          </Control>
          <Control label='Return Component'>
            <Controller
              control={control}
              name={`${fieldName}.returnComponent`}
              render={({ field: { onChange, value } }) => {
                return (
                  <ReturnComponentSearch
                    filter='pullforward'
                    testid={`${fieldName}.return-component-search`}
                    onChange={(val) => {
                      setValue(`${fieldName}.name`, val.name);
                      onChange(val.value);
                      trigger(`${fieldName}.returnComponent`);
                    }}
                    onBlur={() => {
                      trigger(`${fieldName}.returnComponent`);
                    }}
                    value={
                      timeWindows[index].returnComponent
                        ? {
                            ...timeWindows[index],
                            value: timeWindows[index].returnComponent,
                            label: timeWindows[index].name,
                          }
                        : ''
                    }
                    hasError={getErrorMessage(`${fieldName}.returnComponent`)}
                    style={{ width: 200 }}
                  />
                );
              }}
            />
          </Control>
          <Control label='Number of Periods'>
            <Input
              {...register(`${fieldName}.numberPeriods`)}
              placeholder='Enter number'
              data-testid={`${fieldName}.numberPeriods`}
              errorMessage={getErrorMessage(`${fieldName}.numberPeriods`)}
              style={{ width: 200 }}
            />
          </Control>
        </div>
        <div
          style={{ display: 'flex', flexDirection: 'row', alignSelf: 'center' }}
        >
          {hasErrors && <Warning size='13px' className='m-1' fill='#AA0101' />}
          <CircleButton
            key='clone'
            data-testid={`${fieldName}-clone`}
            icon={<Clone size='7px' />}
            title='Clone'
            onClick={() => {
              onClone?.({ ...getValues(fieldName) });
            }}
          />
          <CircleButton
            disabled={timeWindows.length === 1}
            key='delete'
            data-testid={`${fieldName}-delete`}
            icon={<Delete size='7px' />}
            title='Delete'
            onClick={() => {
              onRemove?.(index);
            }}
          />
        </div>
      </TimeWindowHeaderWrapper>
    </div>
  );
};
